/**
 * Modelo de domínio do Entreletras.
 *
 * A nomenclatura segue o glossário da spec (§3) em português de propósito:
 * `PalavraBase`, `Ponte`, `Sugestao`, `Ancora`, `Draft`, `Cadeia`. Ver ADR-0003.
 * Todo tipo aqui é imutável e serializável — o núcleo não guarda referências
 * a objetos do navegador.
 */

/**
 * Modo de jogo (§22). Todos rodam sobre o mesmo tabuleiro em cadeia — palavra
 * horizontal, vertical cruzando, âncora, encaixe espacial. O que muda é o que
 * a partida cobra por cima disso.
 *
 * O identificador não é o nome de tela: quem traduz um no outro é `NOME_MODO`,
 * e só ele. Estes identificadores estão gravados em toda sessão salva e nos
 * critérios da spec, então não acompanham as trocas de nome da interface.
 *
 * - `termo` (`Trama` na tela): há uma palavra escondida, e cada palavra
 *   escrita é um palpite.
 * - `zen` (`Bistrô`): cadeia infinita, sem fim e sem placar.
 * - `corre` (`Nitro`): o tempo é fixo, e o placar é quantas palavras cabem nele.
 * - `trama` (`Átrio`): as jogadas são fixas, e o placar é o quanto se tira delas.
 * - `palavredo`: arcade de níveis sem fim, cada um com o seu desafio.
 */
export type ModoJogo = 'termo' | 'zen' | 'corre' | 'trama' | 'palavredo';

/**
 * Modos oferecidos na escolha; os demais são de desenvolvimento (§22.5).
 *
 * Públicos: `Trama`, `Bistrô` e `Palavredo`. Atrás do portão: `Nitro` e
 * `Átrio`.
 */
export const MODOS_PUBLICOS: readonly ModoJogo[] = ['termo', 'zen', 'palavredo'];
export const MODOS_DEV: readonly ModoJogo[] = ['corre', 'trama'];

export const MODO_PADRAO: ModoJogo = 'termo';

/**
 * Todo modo que existe, num lugar só.
 *
 * O `satisfies` é o ponto: acrescentar um modo ao tipo sem registrá-lo aqui
 * **quebra o build**. Isso não é zelo — é a correção de um defeito real. As
 * listas de modos aceitos estavam repetidas em três lugares (o tipo, a leitura
 * da sessão e a preferência salva), e o Palavredo entrou em dois deles: o modo
 * era gravado, recusado na volta como desconhecido, e a pessoa voltava à Trama
 * sem que nada avisasse. A lista repetida à mão é que era o defeito; esta é a
 * única, e quem quiser divergir dela não compila.
 */
const TODOS_OS_MODOS = {
  termo: true,
  zen: true,
  corre: true,
  trama: true,
  palavredo: true,
} as const satisfies Record<ModoJogo, true>;

/** O valor lido do disco, da URL ou de qualquer borda é um modo conhecido? */
export function ehModoJogo(valor: unknown): valor is ModoJogo {
  return typeof valor === 'string' && Object.hasOwn(TODOS_OS_MODOS, valor);
}

/** O modo está disponível? Fora do modo de desenvolvimento, só os públicos. */
export function modoDisponivel(modo: ModoJogo, dev: boolean): boolean {
  return dev || MODOS_PUBLICOS.includes(modo);
}

/** Forma canônica de uma palavra: NFC, caixa-alta, diacríticos preservados. */
export type PalavraCanonica = string & { readonly __marca: 'PalavraCanonica' };

/** Um único caractere canônico do tabuleiro (inclui `-`, que não é jogável). */
export type Caractere = string & { readonly __marca: 'Caractere' };

/** Coordenada inteira da grade. Coluna cresce para a direita, linha para baixo. */
export interface Celula {
  readonly coluna: number;
  readonly linha: number;
}

export type Orientacao = 'horizontal' | 'vertical';

/** Papel de uma palavra já confirmada na cadeia (§3). */
export type PapelPalavra = 'base' | 'ponte';

export type IdPalavra = string;

/** Palavra confirmada e fixada no tabuleiro. */
export interface PalavraColocada {
  readonly id: IdPalavra;
  readonly texto: PalavraCanonica;
  /** Célula da primeira letra. */
  readonly origem: Celula;
  readonly orientacao: Orientacao;
  readonly papel: PapelPalavra;
  /** Posição na cadeia, começando em 0. */
  readonly turno: number;
}

/**
 * Sugestão vertical temporária (§3, §4.2). Existe apenas em memória enquanto o
 * draft está ativo; ao ser escolhida vira uma `PalavraColocada` de papel `ponte`.
 */
export interface Sugestao {
  /** Estável para a mesma célula de cruzamento — chave de render e de teste. */
  readonly id: string;
  readonly texto: PalavraCanonica;
  /** Célula da palavra-base onde a sugestão cruza. */
  readonly cruzamento: Celula;
  /** Índice (0-based) da letra cruzada dentro da palavra-base. */
  readonly indiceNaBase: number;
  /** Índice (0-based) da letra cruzada dentro do texto da sugestão. */
  readonly indiceNoTexto: number;
  /** Célula da primeira letra da sugestão. */
  readonly origem: Celula;
}

/**
 * Coluna sem candidato válido. A spec exige marcador explícito de
 * indisponibilidade, e não ausência silenciosa (T02-01, §17).
 */
export interface ColunaIndisponivel {
  readonly cruzamento: Celula;
  readonly indiceNaBase: number;
  readonly motivo: 'sem-candidato' | 'hifen' | 'ja-cruzada';
}

export type ColunaSugestao =
  | { readonly tipo: 'sugestao'; readonly sugestao: Sugestao }
  | { readonly tipo: 'indisponivel'; readonly coluna: ColunaIndisponivel };

/** Letra da ponte escolhida para iniciar a próxima palavra-base (§3). */
export interface Ancora {
  readonly celula: Celula;
  readonly caractere: Caractere;
  /** Índice da letra dentro da ponte. */
  readonly indiceNaPonte: number;
  readonly idPonte: IdPalavra;
}

/** Sequência linear de palavras confirmadas (§3). */
export interface Cadeia {
  readonly palavras: readonly PalavraColocada[];
}

/**
 * Por que uma palavra não pôde ser colocada (§9).
 *
 * Mora aqui, e não em `tabuleiro/colisao`, porque deixou de ser detalhe do
 * verificador: §7.3 usa o motivo para dizer ao jogador o que houve.
 */
export type MotivoColisao =
  | 'cruzamento-fora-de-lugar'
  | 'letra-diferente-no-cruzamento'
  | 'sobreposicao-paralela'
  | 'perpendicular-invalida'
  | 'sem-respiro';

/** Encaixe candidato do draft ancorado (§4.1, T05-05, T06-04). */
export interface Encaixe {
  /** Índice da ocorrência da âncora dentro do draft. */
  readonly indiceNoDraft: number;
  readonly origem: Celula;
  /** Pontuação espacial; maior é melhor. Ver `pontuarEncaixe`. */
  readonly pontuacao: number;
  readonly valido: boolean;
  /** Só quando `valido` é falso: o que barrou este encaixe (T05-05). */
  readonly motivo?: MotivoColisao;
  /** Só em `perpendicular-invalida`: a sequência que se formaria. */
  readonly formada?: string;
}

/** Retângulo em coordenadas de grade. */
export interface Regiao {
  readonly colunaMin: number;
  readonly linhaMin: number;
  readonly colunaMax: number;
  readonly linhaMax: number;
}

/** Câmera do tabuleiro (§7.2). */
export interface Camera {
  /** Deslocamento em pixels de tela. */
  readonly x: number;
  readonly y: number;
  /** 0.5 .. 2.0 (T07-03). */
  readonly zoom: number;
}
